import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { Folder } from '@lockton-apps/shared/interfaces';
import {
  ClientDocumentsActions,
  ClientDocumentsEffectsActions,
} from '@lockton-apps/clients/actions';
import { DashboardContactActions } from '@lockton-apps/dashboard/action';
import { FoldersApiActions } from '@lockton-apps/shared/action/folders';

import * as FoldersActions from './folders.actions';
import { PolicyDetailsActions } from '@lockton-apps/policies/action';

export const FOLDERS_FEATURE_KEY = 'folders';

export interface FoldersState extends EntityState<Folder> {
  selectedId?: number | null; // which Folders record has been selected
  loaded: boolean; // has the Folders list been loaded
  error?: string | null; // last known error (if any)
  folderInUpdate: Folder | null;
}

export interface FoldersPartialState {
  readonly [FOLDERS_FEATURE_KEY]: FoldersState;
}

export const foldersAdapter: EntityAdapter<Folder> =
  createEntityAdapter<Folder>();

export const initialFoldersState: FoldersState = foldersAdapter.getInitialState(
  {
    loaded: false,
    folderInUpdate: null,
  }
);

const reducer = createReducer(
  initialFoldersState,
  on(
    ClientDocumentsActions.init,
    DashboardContactActions.init,
    DashboardContactActions.documentsClientSelected,
    PolicyDetailsActions.init,
    (state) => ({
      ...state,
      loaded: false,
      error: null,
    })
  ),
  on(FoldersApiActions.clientFoldersLoadedSuccess, (state, { folders }) =>
    foldersAdapter.setAll(folders, { ...state, loaded: true })
  ),
  on(
    FoldersApiActions.clientFoldersLoadedFailure,
    FoldersApiActions.addedFailure,
    (state, { error }) => ({
      ...state,
      loaded: true,
      error,
    })
  ),

  on(
    ClientDocumentsEffectsActions.navigated, //#TODO remove as this should work through router
    DashboardContactActions.folderSelected,
    (state, { folderId }) => ({
      ...state,
      selectedId: folderId,
    })
  ),

  on(
    ClientDocumentsActions.folderUpdated,
    PolicyDetailsActions.folderEditted,
    (state, { folder }) =>
      foldersAdapter.updateOne(
        { id: folder.id as number, changes: folder },
        {
          ...state,
          folderInUpdate: state.entities[folder.id as number] as Folder,
        }
      )
  ),
  on(
    ClientDocumentsActions.folderDeleted,
    PolicyDetailsActions.folderDeleted,
    (state, { id }) =>
      foldersAdapter.removeOne(id, {
        ...state,
        folderInUpdate: state.entities[id] as Folder,
      })
  ),
  on(
    FoldersApiActions.updatedFailure,
    FoldersApiActions.deletedFailure,
    (state) => foldersAdapter.setOne(state.folderInUpdate as Folder, state)
  ),

  on(
    FoldersActions.addFromDocumentDialogSuccess,
    FoldersApiActions.addedSuccess,
    (state, { folder }) => foldersAdapter.addOne(folder, { ...state })
  ),

  on(DashboardContactActions.destroyed, (state) => ({
    ...state,
    selectedId: null,
  }))
);

export function foldersReducer(
  state: FoldersState | undefined,
  action: Action
) {
  return reducer(state, action);
}
